const BIRTHDATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const POPUP_SUBMIT_KEY = 'nl-popup-submitted';

/**
 * @param {HTMLFormElement} form
 * @returns {string}
 */
function getBirthdateStorageKey(form) {
  return `nl-birthdate-${form.id}`;
}

/**
 * @param {HTMLFormElement} form
 * @returns {string}
 */
function getProfilingStorageKey(form) {
  return `nl-profiling-${form.id}`;
}

/**
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function isNewsletterSuccess(form) {
  return form.closest('.shopify-nl-form')?.hasAttribute('data-nl-posted-successfully') ?? false;
}

/**
 * @param {HTMLFormElement} form
 * @returns {string}
 */
function getRestoreStorageKey(form) {
  return `nl-restore-${form.id}`;
}

/**
 * @param {HTMLFormElement} form
 */
function markFormForRestore(form) {
  sessionStorage.setItem(getRestoreStorageKey(form), '1');
}

/**
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function shouldRestoreForm(form) {
  return sessionStorage.getItem(getRestoreStorageKey(form)) === '1';
}

/**
 * @param {HTMLFormElement} form
 */
function clearNewsletterFormStorage(form) {
  sessionStorage.removeItem(getBirthdateStorageKey(form));
  sessionStorage.removeItem(getProfilingStorageKey(form));
  sessionStorage.removeItem(getRestoreStorageKey(form));
}

/**
 * @param {HTMLFormElement} form
 * @param {HTMLInputElement} birthdateInput
 * @param {HTMLInputElement | null} profilingInput
 */
function restoreFormFields(form, birthdateInput, profilingInput) {
  if (isNewsletterSuccess(form)) {
    clearNewsletterFormStorage(form);
    return;
  }

  if (!shouldRestoreForm(form)) {
    clearNewsletterFormStorage(form);
    return;
  }

  const savedBirthdate = sessionStorage.getItem(getBirthdateStorageKey(form));

  if (savedBirthdate && !birthdateInput.value.trim()) {
    birthdateInput.value = savedBirthdate;
  }

  if (profilingInput instanceof HTMLInputElement) {
    const savedProfiling = sessionStorage.getItem(getProfilingStorageKey(form));

    if (savedProfiling === 'true') {
      profilingInput.checked = true;
    } else if (savedProfiling === 'false') {
      profilingInput.checked = false;
    }
  }

  clearNewsletterFormStorage(form);
}

/**
 * @param {string} value
 * @returns {string}
 */
function formatBirthdate(value) {
  const digits = value.replace(/\D/g, '').slice(0, 8);

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * @param {HTMLInputElement} input
 * @param {string} formatted
 */
function setBirthdateValue(input, formatted) {
  const cursorFromEnd = input.value.length - input.selectionStart;
  input.value = formatted;

  if (document.activeElement !== input) return;

  let nextCursor = formatted.length - cursorFromEnd;
  if (nextCursor < 0) nextCursor = 0;
  if (nextCursor > formatted.length) nextCursor = formatted.length;

  input.setSelectionRange(nextCursor, nextCursor);
}

/**
 * @param {string} value
 * @returns {boolean}
 */
function isValidBirthdate(value) {
  const match = value.trim().match(BIRTHDATE_PATTERN);
  if (!match) return false;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

/**
 * @param {string} value
 * @returns {boolean}
 */
function isBirthdateRequiredAndValid(value) {
  const trimmed = value.trim();

  if (!trimmed) return false;

  return isValidBirthdate(trimmed);
}

/**
 * @param {HTMLParagraphElement | null} birthdateError
 * @param {'required' | 'invalid'} type
 */
function showBirthdateError(birthdateError, type) {
  if (!(birthdateError instanceof HTMLElement)) return;

  const message =
    type === 'required'
      ? birthdateError.dataset.errorRequired
      : birthdateError.dataset.errorInvalid;

  if (message) {
    birthdateError.textContent = message;
  }

  birthdateError.hidden = false;
}

/**
 * @param {string} baseTags
 * @param {string} birthdate
 * @param {boolean} profilingChecked
 * @returns {string}
 */
function buildTags(baseTags, birthdate, profilingChecked) {
  const profiling = profilingChecked ? 'true' : 'false';

  return `${baseTags}, birthdate: ${birthdate}, profiling:${profiling}`;
}

/**
 * @param {object} context
 * @returns {boolean}
 */
function validateAndPrepareForm(context) {
  const {
    form,
    emailInput,
    birthdateInput,
    birthdateError,
    tagsInput,
    profilingInput,
    baseTags,
    isPopupForm,
  } = context;

  if (emailInput instanceof HTMLInputElement && !emailInput.checkValidity()) {
    form.reportValidity();
    return false;
  }

  const birthdate = birthdateInput.value.trim();

  if (!birthdate) {
    showBirthdateError(birthdateError, 'required');
    birthdateInput.focus();
    return false;
  }

  if (!isBirthdateRequiredAndValid(birthdate)) {
    showBirthdateError(birthdateError, 'invalid');
    birthdateInput.focus();
    return false;
  }

  tagsInput.value = buildTags(
    baseTags,
    birthdate,
    profilingInput instanceof HTMLInputElement && profilingInput.checked
  );

  sessionStorage.setItem(getBirthdateStorageKey(form), birthdate);

  if (profilingInput instanceof HTMLInputElement) {
    sessionStorage.setItem(
      getProfilingStorageKey(form),
      profilingInput.checked ? 'true' : 'false'
    );
  }

  markFormForRestore(form);

  if (isPopupForm) {
    sessionStorage.setItem(POPUP_SUBMIT_KEY, '1');
  }

  return true;
}

/**
 * @param {HTMLFormElement} form
 */
function initNewsletterForm(form) {
  if (form.dataset.nlInitialized === 'true') return;

  const wrapper = form.closest('.shopify-nl-form');
  const tagsInput = form.querySelector('[data-nl-tags-input]');
  const birthdateInput = form.querySelector('[data-nl-birthdate]');
  const profilingInput = form.querySelector('[data-nl-profiling]');
  const birthdateError = form.querySelector('[data-nl-birthdate-error]');
  const emailInput = form.querySelector('[name="contact[email]"]');
  const submitButtons = form.querySelectorAll('[type="submit"]');

  if (
    !(tagsInput instanceof HTMLInputElement) ||
    !(birthdateInput instanceof HTMLInputElement)
  ) {
    return;
  }

  form.dataset.nlInitialized = 'true';

  const context = {
    form,
    emailInput,
    birthdateInput,
    birthdateError,
    tagsInput,
    profilingInput,
    baseTags: tagsInput.dataset.baseTags || tagsInput.value,
    isPopupForm: wrapper?.classList.contains('shopify-nl-form--popup'),
  };

  restoreFormFields(form, birthdateInput, profilingInput);

  birthdateInput.addEventListener('input', (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    setBirthdateValue(event.target, formatBirthdate(event.target.value));

    if (birthdateError instanceof HTMLElement) {
      birthdateError.hidden = true;
    }
  });

  submitButtons.forEach((button) => {
    button.addEventListener(
      'click',
      (event) => {
        if (!validateAndPrepareForm(context)) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  });

  form.addEventListener(
    'submit',
    (event) => {
      if (!validateAndPrepareForm(context)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );
}

function initNewsletterForms() {
  document
    .querySelectorAll('form.shopify-nl-form__form')
    .forEach(initNewsletterForm);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNewsletterForms);
} else {
  initNewsletterForms();
}
